import { useState } from 'react';
import { LogOut, Settings, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { CanvasStudio } from './CanvasStudio';
import { LinkCreationForm } from './LinkCreationForm';
import { LinkList } from './LinkList';
import { SettingsPage } from './SettingsPage';

export function Dashboard() {
  const { user, signOut } = useAuth();
  const { appName, appVersion, footerText } = useBranding();
  const [mergedImage, setMergedImage] = useState<File | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showSettings, setShowSettings] = useState(false);

  const handleImageGenerated = (file: File) => {
    setMergedImage(file);
  };

  const handleLinkCreated = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleClearImage = () => {
    setMergedImage(null);
  };

  return (
    <div className="min-h-screen bg-black">
      <header className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-lg flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">
                  {appName}
                </h1>
                <p className="text-slate-400 text-xs">{appVersion}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="text-right mr-4">
                <p className="text-slate-400 text-sm">{user?.email}</p>
              </div>
              <button
                onClick={() => setShowSettings(true)}
                className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition"
                title="Settings"
              >
                <Settings className="w-5 h-5" />
              </button>
              <button
                onClick={signOut}
                className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition"
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <CanvasStudio onImageGenerated={handleImageGenerated} />
          <LinkCreationForm
            mergedImage={mergedImage}
            onLinkCreated={handleLinkCreated}
            onClearImage={handleClearImage}
          />
        </div>

        <LinkList refreshTrigger={refreshTrigger} />
      </main>

      <footer className="bg-slate-900 border-t border-slate-800 py-6 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-2 text-slate-400 text-sm">
            <span>{footerText}</span>
            <span className="text-slate-600">•</span>
            <span className="text-emerald-400 font-semibold">v25.0 Ultimate</span>
          </div>
        </div>
      </footer>

      {showSettings && <SettingsPage onClose={() => setShowSettings(false)} />}
    </div>
  );
}
